import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { currentUserHasMoney, MONEY_MASK } from "../../lib/permissions";

const useStyles = makeStyles(() => ({
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    position: "relative",
    zIndex: 1,
  },
  table: {
    minWidth: "100%",
    borderCollapse: "collapse",
    border: "1px solid #e0e0e0",
    tableLayout: "fixed",
  },
  tableHeader: {
    backgroundColor: "#f3f4f6",
    color: "#333",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  tableCell: {
    border: "1px solid #e0e0e0",
    padding: "12px 16px",
    textAlign: "left",
    minWidth: "150px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  oddRow: {
    backgroundColor: "#fff",
  },
  evenRow: {
    backgroundColor: "#f9fafb",
  },
  subHeader: {
    backgroundColor: "#e5e7eb",
    textAlign: "center",
    fontWeight: "bold",
    position: "sticky",
    top: "43px",
    zIndex: 1,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
  },
  paginationButton: {
    margin: "0 5px",
    padding: "8px 12px",
    cursor: "pointer",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    backgroundColor: "#fff",
    "&:hover:not(:disabled)": {
      backgroundColor: "#f0f0f0",
    },
    "&:disabled": {
      cursor: "not-allowed",
      color: "#aaa",
    },
    '&[style*="bold"]': {
      backgroundColor: "#007BFF",
      color: "#fff",
      fontWeight: "bold",
    },
  },
}));

export const formatCurrency = (value) => {
  // financials.view gate: mask monetary values for users without it.
  if (!currentUserHasMoney()) return MONEY_MASK;
  if (typeof value !== "number") {
    console.error("Invalid value type for currency formatting:", value);
    return value;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "NGN",
  }).format(value);
};

const safeFormatCurrency = (value) => {
  const numericValue = parseFloat(value);
  return !isNaN(numericValue) ? formatCurrency(numericValue) : value;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-CA");
};

// Money keys — anything that represents currency. Backend fix landed the
// three avg_* revenue fields (previously wrong tiny fractions, now real
// money) plus the new total_net_revenue. Adding all of them here.
const MONEY_KEYS = new Set([
  "net_salary",
  "payment",
  "fees",
  "total_payment",
  "total_amount",
  "total_salaries",
  "total_request_amounts",
  "total_revenue_from_fees",
  "total_requested_amount",
  "total_net_revenue",
  "avg_requested_amount_per_transaction",
  "avg_fees_per_transaction",
  "avg_net_revenue_per_transaction",
]);

// Percent-rate keys — render as `X%`. `rate_avg_fees_per_transaction` is now
// a real fee rate (fees / requested_amount * 100), not the previous
// meaningless value; treating it as a percent keeps the "Fee Rate" column
// truthful.
const PERCENT_KEYS = new Set([
  "rate_registered_employees",
  "rate_active_employees_from_registered",
  "rate_active_employees_from_total",
  "rate_avg_fees_per_transaction",
  "active_users_change_rate",
  "monthly_active_users_change_rate",
  "monthly_active_users_rate_total",
  "monthly_active_users_rate_active",
  "change_in_total_revenue_rate",
  "revenue_rate",
  "change_in_revenue_rate",
  "request_rate_from_total_salaries",
  "request_rate_from_total_active_salaries",
]);

const formatPercent = (value) => {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return value;
  // Trim trailing zeros so 12.00 → 12% and 12.34 → 12.34%.
  const rounded = Math.round(n * 100) / 100;
  return `${rounded}%`;
};

const TableComponent = ({ data, columns, endpoint }) => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 12;

  const handleNextPage = () => {
    if (currentPage * rowsPerPage < data.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = Array.isArray(data) ? data.slice(startIndex, startIndex + rowsPerPage) : new Array()

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
      .filter((page) => {
        return (
          page === 1 ||
          page === totalPages ||
          (page >= currentPage - 2 && page <= currentPage + 2)
        );
      })
      .reduce((acc, page, i, arr) => {
        if (i > 0 && page !== arr[i - 1] + 1) {
          acc.push("...");
        }
        acc.push(page);
        return acc;
      }, []);
  };

  if (endpoint === "transactionanalysis") {
    const ranges = data[0]?.ranges || [];

    return (
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.tableHeader}>
              <th className={classes.tableCell} rowSpan="2">
                Period
              </th>
              {ranges.map((range, index) => (
                <th key={index} className={classes.tableCell} colSpan="2">
                  {range.range_label}
                </th>
              ))}
              <th className={classes.tableCell} rowSpan="2">
                Total Transactions
              </th>
              <th className={classes.tableCell} rowSpan="2">
                Total Amount
              </th>
            </tr>
            <tr className={classes.subHeader}>
              {ranges.map((_, index) => (
                <React.Fragment key={index}>
                  <th className={classes.tableCell}>Number</th>
                  <th className={classes.tableCell}>Rate</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, index) => (
              <tr
                key={index}
                className={index % 2 === 0 ? classes.oddRow : classes.evenRow}
              >
                <td className={classes.tableCell}>{row.period}</td>
                {row?.ranges?.map((range, rangeIndex) => (
                  <React.Fragment key={rangeIndex}>
                    <td className={classes.tableCell}>{range.number}</td>
                    <td className={classes.tableCell}>{range.rate}%</td>
                  </React.Fragment>
                ))}
                <td className={classes.tableCell}>{row.total_transactions}</td>
                <td className={classes.tableCell}>{safeFormatCurrency(row.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {data.length > rowsPerPage && (
          <div className={classes.pagination}>
            <button
              className={classes.paginationButton}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                className={classes.paginationButton}
                onClick={() =>
                  typeof page === "number" && handlePageChange(page)
                }
                style={{
                  fontWeight: currentPage === page ? "bold" : "normal",
                  cursor: page === "..." ? "default" : "pointer",
                }}
                disabled={page === "..."}
              >
                {page}
              </button>
            ))}
            <button
              className={classes.paginationButton}
              onClick={handleNextPage}
              disabled={currentPage * rowsPerPage >= data.length}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.tableWrapper}>
      <table className={classes.table}>
        <thead>
          <tr className={classes.tableHeader}>
            {columns.map((col, index) => (
              <th key={index} className={classes.tableCell}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentRows.map((row, index) => (
            <tr
              key={index}
              className={index % 2 === 0 ? classes.oddRow : classes.evenRow}
            >
              {columns.map((col, colIndex) => {
                // Column label → row key. Special-case the display-only
                // header rename: "Fee Rate" reads
                // `rate_avg_fees_per_transaction` from the row.
                let key = col.replace(/ /g, "_").toLowerCase();
                if (key === "fee_rate") key = "rate_avg_fees_per_transaction";
                // "Total Users Growth" / "*Growth Rate" come from the same
                // backend fields as before — the label change is UI-only,
                // so re-map to the source keys.
                if (key === "total_users_growth") key = "total_users_change";
                if (key === "active_users_growth_rate") key = "active_users_change_rate";
                if (key === "monthly_active_users_growth_rate") key = "monthly_active_users_change_rate";
                const value = row[key];

                let formattedValue;

                if (key === "start_date") {
                  formattedValue = formatDate(value);
                } else if (MONEY_KEYS.has(key)) {
                  formattedValue = safeFormatCurrency(value);
                } else if (PERCENT_KEYS.has(key)) {
                  formattedValue = value == null || value === "" ? "—" : formatPercent(value);
                } else {
                  formattedValue = value;
                }

                return (
                  <td key={colIndex} className={classes.tableCell}>
                    {Array.isArray(formattedValue)
                      ? formattedValue.map((item, idx) => (
                        <div key={idx}>
                          {Object.entries(item).map(
                            ([itemKey, itemValue]) => (
                              <div key={itemKey}>
                                {itemKey}: {itemValue}
                              </div>
                            )
                          )}
                        </div>
                      ))
                      : typeof formattedValue === "object" &&
                        formattedValue !== null
                        ? Object.entries(formattedValue).map(
                          ([objectKey, objectValue], idx) => (
                            <div key={idx}>
                              {objectKey}: {safeFormatCurrency(objectValue)}
                            </div>
                          )
                        )
                        : formattedValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {data.length > rowsPerPage && (
        <div className={classes.pagination}>
          <button
            className={classes.paginationButton}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              className={classes.paginationButton}
              onClick={() => typeof page === "number" && handlePageChange(page)}
              style={{
                fontWeight: currentPage === page ? "bold" : "normal",
                cursor: page === "..." ? "default" : "pointer",
              }}
              disabled={page === "..."}
            >
              {page}
            </button>
          ))}
          <button
            className={classes.paginationButton}
            onClick={handleNextPage}
            disabled={currentPage * rowsPerPage >= data.length}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
